import classNames from 'classnames';
import { useIsEditor } from 'modules/archive';
import { AuthShowContainer, AuthorizedContent } from 'modules/auth';
import { PROJECT_CAMPSCAPES } from 'modules/constants';
import { GalleryContainer } from 'modules/gallery';
import { HelpText } from 'modules/help-text';
import { useI18n } from 'modules/i18n';
import {
    DestroyTranscript,
    MarkTextFormContainer,
    UploadEditTableContainer,
    UploadTranscriptContainer,
} from 'modules/interview-actions';
import { InterviewMap } from 'modules/interview-map';
import {
    CitationInfoContainer,
    InterviewContributorsContainer,
    InterviewInfoContainer,
    InterviewLinksField,
    InterviewTextMaterialsContainer,
} from 'modules/interview-metadata';
import { MaterialList } from 'modules/materials';
import { PersonDataContainer, usePersonWithAssociations } from 'modules/person';
import { ErrorBoundary } from 'modules/react-toolbox';
import { SelectedRegistryReferencesContainer } from 'modules/registry-references';
import { usePathBase, useProject } from 'modules/routes';
import { Spinner } from 'modules/spinners';
import { InlineNotification } from 'modules/ui';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import AdminActions from './AdminActions';
import AdminSubTab from './AdminSubTab';
import DownloadLinks from './DownloadLinks';
import SubTab from './SubTab';

/**
 * Shows interview metadata and tools according to project display settings and access checks.
 * Waits for project details before applying catalog and map visibility settings.
 *
 * @param {Object} props - Interview context and sidebar actions.
 * @param {string} [props.archiveId] - Public interview identifier.
 * @param {string} [props.projectId] - Current project's shortname.
 * @param {Object} [props.interview] - Loaded interview.
 * @param {number} [props.intervieweeId] - Interviewee's internal ID.
 * @param {Function} props.setViewMode - Selects the search results view.
 * @param {Function} props.hideSidebar - Closes the sidebar.
 * @param {boolean} props.isLoggedIn - Whether the visitor is signed in.
 * @returns {React.ReactElement|null} Interview tools, loading/error state, or nothing without an interview.
 */
export default function InterviewTabPanel({
    archiveId,
    projectId,
    interview,
    intervieweeId,
    setViewMode,
    hideSidebar,
    isLoggedIn,
}) {
    const pathBase = usePathBase();
    const { project, isLoading, error } = useProject();
    const isEditor = useIsEditor();
    const { t } = useI18n();
    const { data: interviewee, isLoading: intervieweeIsLoading } =
        usePersonWithAssociations(intervieweeId);

    const searchPath = `${pathBase}/searches/archive?fulltext=${archiveId}`;

    if (!archiveId || archiveId === 'new' || !projectId || !interview) {
        return null;
    }

    if (error) {
        return (
            <InlineNotification
                variant="error"
                title={t('error')}
                description={error.message}
                isClosable={false}
            />
        );
    }
    if (isLoading || !project) return <Spinner />;

    const hasPublicPhotos =
        interview.photos &&
        Object.values(interview.photos).length > 0 &&
        Object.values(interview.photos).some(
            (photo) => photo.workflow_state === 'public'
        );
    const showGallerySection = hasPublicPhotos || isEditor;

    const hasMaterials =
        interview.material_count && interview.material_count > 0;
    const showMaterialSection = hasMaterials || isEditor;

    return (
        <ErrorBoundary small>
            <h3 className="SidebarTabs-title">{t('interview')}</h3>
            <div
                className={classNames(
                    'flyout-sub-tabs-container',
                    'flyout-video',
                    {
                        hidden: projectId === PROJECT_CAMPSCAPES,
                    }
                )}
            >
                <AuthorizedContent object={interview} action="update">
                    <p>
                        <Link
                            onClick={() => {
                                setViewMode('workflow');
                                hideSidebar();
                            }}
                            to={searchPath}
                        >
                            {t('workflow')}
                        </Link>
                    </p>
                </AuthorizedContent>
                {!project.is_catalog && (
                    <>
                        <AuthorizedContent
                            object={interview}
                            action="show"
                            showIfPublic
                        >
                            <SubTab title={t('person_info')} open={!isLoggedIn}>
                                {isEditor && (
                                    <HelpText
                                        code="interview_person_data"
                                        small
                                    />
                                )}
                                <PersonDataContainer />
                                {intervieweeIsLoading ? (
                                    <Spinner />
                                ) : (
                                    interviewee && (
                                        <SelectedRegistryReferencesContainer
                                            refObject={interviewee}
                                        />
                                    )
                                )}
                            </SubTab>
                        </AuthorizedContent>
                        <AuthShowContainer ifLoggedOut ifNoProject>
                            <AuthorizedContent
                                object={interview}
                                action="show"
                                showIfPublic
                            >
                                <SubTab
                                    title={t('interview_info')}
                                    open={!isLoggedIn}
                                >
                                    <InterviewInfoContainer />
                                    <InterviewContributorsContainer />
                                </SubTab>
                            </AuthorizedContent>
                        </AuthShowContainer>
                        <AuthShowContainer hasProjectAccess>
                            <AuthorizedContent
                                object={interview}
                                action="show"
                                showIfPublic
                            >
                                <SubTab title={t('interview_info')}>
                                    {isEditor && (
                                        <HelpText
                                            code="interview_interview_data"
                                            small
                                        />
                                    )}
                                    <InterviewInfoContainer />
                                    <InterviewContributorsContainer />
                                    <InterviewTextMaterialsContainer />
                                    <InterviewLinksField
                                        interview={interview}
                                    />
                                </SubTab>
                            </AuthorizedContent>
                        </AuthShowContainer>
                    </>
                )}
                <AuthorizedContent
                    object={{ type: 'Segment', interview_id: interview.id }}
                    action="update"
                >
                    <SubTab title={t('edit.upload_transcript.title')}>
                        <HelpText
                            code="interview_upload_transcript"
                            className="u-mb"
                        />
                        <UploadTranscriptContainer />
                    </SubTab>
                </AuthorizedContent>

                {interview.alpha3s_with_transcript &&
                    interview.alpha3s_with_transcript.length > 0 && (
                        <AuthorizedContent
                            object={{
                                type: 'Segment',
                                interview_id: interview.id,
                            }}
                            action="update"
                        >
                            <SubTab title={t('edit.destroy_transcript.title')}>
                                <HelpText
                                    code="interview_destroy_transcript"
                                    className="u-mb"
                                />
                                <DestroyTranscript interview={interview} />
                            </SubTab>
                        </AuthorizedContent>
                    )}

                <AuthorizedContent object={interview} action="update">
                    <SubTab title={t('edit.upload_edit_table.title')}>
                        <UploadEditTableContainer />
                    </SubTab>
                </AuthorizedContent>

                {/* TODO: rework assign speaker functionality
                <AuthorizedContent object={interview} action='update_speakers'>
                    <SubTab title={t('assign_speakers')}>
                        <AssignSpeakersFormContainer interview={interview} />
                    </SubTab>
                </AuthorizedContent>*/}

                <AuthorizedContent object={interview} action="mark_texts">
                    <SubTab title={t('mark_texts')}>
                        <MarkTextFormContainer interview={interview} />
                    </SubTab>
                </AuthorizedContent>

                {projectId !== PROJECT_CAMPSCAPES && (
                    <AuthShowContainer hasProjectAccess>
                        <AuthShowContainer hasProjectAccess={project.has_map}>
                            <AuthorizedContent
                                object={interview}
                                action="show"
                                showIfPublic
                            >
                                <SubTab title={t('map')}>
                                    {isEditor && (
                                        <HelpText
                                            code="interview_map"
                                            className="u-mb"
                                        />
                                    )}
                                    <InterviewMap />
                                </SubTab>
                            </AuthorizedContent>
                        </AuthShowContainer>

                        {showGallerySection && (
                            <AuthorizedContent
                                object={interview}
                                action="show"
                                showIfPublic
                            >
                                <SubTab title={t('photos')}>
                                    <GalleryContainer />
                                </SubTab>
                            </AuthorizedContent>
                        )}

                        {showMaterialSection && (
                            <AuthorizedContent
                                object={interview}
                                action="show"
                                showIfPublic
                            >
                                <SubTab title={t('materials')}>
                                    <MaterialList />
                                </SubTab>
                            </AuthorizedContent>
                        )}

                        <AuthorizedContent
                            object={interview}
                            action="show"
                            showIfPublic
                        >
                            <SubTab title={t('citation')}>
                                <CitationInfoContainer />
                            </SubTab>
                        </AuthorizedContent>

                        <AuthorizedContent object={interview} action="update">
                            <SubTab title={t('admin_actions')}>
                                <AdminActions archiveIds={[archiveId]} />
                            </SubTab>
                        </AuthorizedContent>
                    </AuthShowContainer>
                )}
            </div>
            <div className="flyout-sub-tabs-container flyout-video">
                <AdminSubTab
                    title="edit.downloads.title"
                    obj={interview}
                    action="download"
                >
                    <DownloadLinks
                        archiveId={archiveId}
                        numTapes={Number.parseInt(interview.tape_count)}
                        interview={interview}
                    />
                </AdminSubTab>
            </div>
        </ErrorBoundary>
    );
}

InterviewTabPanel.propTypes = {
    archiveId: PropTypes.string,
    projectId: PropTypes.string,
    interview: PropTypes.object,
    intervieweeId: PropTypes.number,
    setViewMode: PropTypes.func.isRequired,
    hideSidebar: PropTypes.func.isRequired,
    isLoggedIn: PropTypes.bool.isRequired,
};
